import React from 'react';
import { Globe2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useLanguage } from '../../contexts/LanguageContext';

interface LanguageToggleProps {
  className?: string;
  showLabel?: boolean;
  variant?: 'button' | 'minimal';
}

const LanguageToggle: React.FC<LanguageToggleProps> = ({
  className,
  showLabel = true,
  variant = 'button',
}) => {
  const { language, setLanguage } = useLanguage();
  const { t } = useTranslation('common');
  const nextLanguage = language === 'ar' ? 'en' : 'ar';
  const nextLanguageLabel =
    nextLanguage === 'ar'
      ? t('language.arabic')
      : t('language.english');
  const currentLabel =
    language === 'ar'
      ? t('language.shortArabic')
      : t('language.shortEnglish');

  return (
    <button
      type='button'
      onClick={() => setLanguage(nextLanguage)}
      className={clsx(
        'inline-flex h-10 items-center justify-center gap-2 rounded-lg text-sm font-semibold transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        variant === 'minimal'
          ? 'w-10 text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800'
          : 'px-3 text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800',
        className
      )}
      aria-label={t('language.switchTo', { language: nextLanguageLabel })}
      title={t('language.switchTo', { language: nextLanguageLabel })}
    >
      <Globe2 className='h-4 w-4' aria-hidden='true' />
      {showLabel ? <span>{currentLabel}</span> : null}
    </button>
  );
};

export default LanguageToggle;
